import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, User, Phone, Lock, UserPlus, Car } from 'lucide-react';
import { authService } from '../services/authService';
import { useApp } from '../contexts/AppContext';
import { ToastNotificationComponent } from '../components/ToastNotification';
import { LanguageSelector } from '../components/LanguageSelector';

interface PasswordRegistrationFormData {
  username: string;
  fullName: string;
  phoneNumber: string;
  primaryPlate: string;
  password: string;
  confirmPassword: string;
}

interface ValidationErrors {
  username?: string;
  fullName?: string;
  phoneNumber?: string;
  primaryPlate?: string;
  password?: string;
  confirmPassword?: string;
  general?: string;
}

const PasswordRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { updateUser } = useApp();
  const [formData, setFormData] = useState<PasswordRegistrationFormData>({
    username: '',
    fullName: '',
    phoneNumber: '',
    primaryPlate: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type, isVisible: true });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Full name validation (minimum 2 words, 2+ characters each)
    if (!formData.fullName.trim()) {
      newErrors.fullName = t('auth.passwordAuth.errors.fullNameRequired');
    } else {
      const nameParts = formData.fullName.trim().split(/\s+/);
      if (nameParts.length < 2) {
        newErrors.fullName = t('auth.passwordAuth.errors.fullNameTwoWords');
      } else if (nameParts.some(part => part.length < 2)) {
        newErrors.fullName = t('auth.passwordAuth.errors.fullNameMinLength');
      }
    }

    // Phone number validation (E.164 format with length limits)
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = t('auth.passwordAuth.errors.phoneNumberRequired');
    } else {
      const phoneRegex = /^\+[1-9]\d{6,14}$/;
      const cleaned = formData.phoneNumber.trim();
      if (!phoneRegex.test(cleaned)) {
        newErrors.phoneNumber = t('auth.passwordAuth.errors.phoneInvalidFormat');
      } else if (cleaned.length < 8) {
        newErrors.phoneNumber = t('auth.passwordAuth.errors.phoneNumberTooShort');
      } else if (cleaned.length > 16) {
        newErrors.phoneNumber = t('auth.passwordAuth.errors.phoneNumberTooLong');
      }
    }

    // Primary plate validation (Israeli format: 123-45-678)
    if (!formData.primaryPlate.trim()) {
      newErrors.primaryPlate = t('auth.passwordAuth.errors.licensePlateRequired');
    } else {
      const plateRegex = /^\d{3}-\d{2}-\d{3}$/;
      if (!plateRegex.test(formData.primaryPlate.trim())) {
        newErrors.primaryPlate = t('auth.passwordAuth.errors.plateInvalidFormat');
      }
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t('auth.passwordAuth.errors.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('auth.passwordAuth.errors.passwordTooShort');
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.passwordAuth.errors.confirmPasswordRequired');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.passwordAuth.errors.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Auto-format phone number
    if (name === 'phoneNumber') {
      // Remove all non-digits except + at the beginning
      let cleaned = value.replace(/[^\d+]/g, '');
      
      // Ensure it starts with +
      if (!cleaned.startsWith('+')) {
        cleaned = '+' + cleaned.replace(/^\+*/, '');
      }
      
      // Limit to E.164 format (max 15 digits after +)
      const digits = cleaned.substring(1);
      if (digits.length > 15) {
        cleaned = '+' + digits.substring(0, 15);
      }
      
      formattedValue = cleaned;
      
      // Auto-populate username with phone number without +
      const phoneWithoutPlus = cleaned.startsWith('+') ? cleaned.slice(1) : cleaned;
      setFormData(prev => ({ ...prev, [name]: formattedValue, username: phoneWithoutPlus }));
      return;
    }
    
    // Auto-format license plate (Israeli format: 123-45-678)
    if (name === 'primaryPlate') {
      // Remove all non-digits
      let cleaned = value.replace(/\D/g, '');
      
      // Limit to 8 digits max
      if (cleaned.length > 8) {
        cleaned = cleaned.substring(0, 8);
      }
      
      // Apply formatting: 123-45-678
      if (cleaned.length >= 6) {
        formattedValue = cleaned.substring(0, 3) + '-' + cleaned.substring(3, 5) + '-' + cleaned.substring(5);
      } else if (cleaned.length >= 3) {
        formattedValue = cleaned.substring(0, 3) + '-' + cleaned.substring(3);
      } else {
        formattedValue = cleaned;
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
    
    // Clear specific error when user starts typing
    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showToast(t('auth.passwordAuth.errors.fixErrorsBelow'), 'error');
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const registerData = {
        username: formData.username.trim(),
        fullName: formData.fullName.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        primaryPlate: formData.primaryPlate.trim(),
        password: formData.password,
        locale: 'en'
      };

      const result = await authService.registerWithPassword(registerData);
      
      if (result.token && result.user) {
        showToast(t('auth.passwordAuth.messages.registrationSuccessful'), 'success');
        
        // Update app context directly with the user data we received
        // No need to call login() since we already have the token and user
        updateUser(result.user);
        
        // Redirect to home page after short delay
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 2000);
      } else {
        setErrors({ general: t('auth.passwordAuth.errors.registrationFailed') });
        showToast(t('auth.passwordAuth.errors.registrationFailed'), 'error');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      
      let errorMessage = t('auth.passwordAuth.errors.unexpectedError');
      
      // Handle specific API errors
      if (error.response?.data?.error) {
        const serverError = error.response.data.error;
        if (serverError.includes('phone number is already registered')) {
          errorMessage = t('auth.passwordAuth.errors.phoneAlreadyRegistered');
          setErrors({ phoneNumber: t('auth.passwordAuth.errors.phoneAlreadyTaken') });
        } else if (serverError.includes('license plate is already registered')) {
          errorMessage = t('auth.passwordAuth.errors.plateAlreadyRegistered');
          setErrors({ primaryPlate: t('auth.passwordAuth.errors.plateAlreadyTaken') });
        } else {
          errorMessage = serverError;
        }
      } else if (error.response?.status === 400) {
        errorMessage = t('auth.passwordAuth.errors.invalidRegistrationData');
      } else if (error.response?.status === 500) {
        errorMessage = t('auth.passwordAuth.errors.serverError');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ general: errorMessage });
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        padding: '2rem',
        width: '100%',
        maxWidth: '400px'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          <div style={{
            backgroundColor: '#3b82f6',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            color: 'white'
          }}>
            <UserPlus size={32} />
          </div>
          <h1 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            margin: '0 0 0.5rem 0',
            color: '#1f2937'
          }}>{t('auth.passwordAuth.titleRegister')}</h1>
          <p style={{
            color: '#6b7280',
            margin: 0
          }}>{t('auth.passwordAuth.messages.joinChargeQueue')}</p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} autoComplete="on">
          {/* Hidden username field for browser password manager */}
          <input
            type="text"
            name="username"
            value={formData.username}
            autoComplete="username"
            style={{ display: 'none' }}
            readOnly
          />

          {/* Full Name Field */}
          <div>
            <label htmlFor="fullName" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              <User size={18} />
              {t('auth.passwordAuth.fullName')}
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder={t('auth.passwordAuth.placeholders.fullNamePlaceholder')}
              autoComplete="name"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.fullName ? '2px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: isLoading ? '#f9fafb' : 'white'
              }}
              disabled={isLoading}
            />
            {errors.fullName && <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.fullName}</span>}
          </div>

          {/* Primary Plate Field - moved before phone number */}
          <div>
            <label htmlFor="primaryPlate" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              <Car size={18} />
              {t('auth.passwordAuth.licensePlate')}
            </label>
            <input
              type="text"
              id="primaryPlate"
              name="primaryPlate"
              value={formData.primaryPlate}
              onChange={handleInputChange}
              placeholder={t('auth.passwordAuth.placeholders.licensePlateUnique')}
              autoComplete="off"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.primaryPlate ? '2px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: isLoading ? '#f9fafb' : 'white'
              }}
              disabled={isLoading}
            />
            {errors.primaryPlate && <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.primaryPlate}</span>}
            <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>
              {t('auth.passwordAuth.helpText.israeliFormat')}
            </small>
          </div>

          {/* Phone Number Field - moved after license plate */}
          <div>
            <label htmlFor="phoneNumber" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              <Phone size={18} />
              {t('auth.passwordAuth.phoneNumber')}
            </label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder={t('auth.passwordAuth.placeholders.phoneNumberUnique')}
              autoComplete="username tel"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: errors.phoneNumber ? '2px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: isLoading ? '#f9fafb' : 'white'
              }}
              disabled={isLoading}
            />
            {errors.phoneNumber && <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.phoneNumber}</span>}
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              <Lock size={18} />
              {t('auth.passwordAuth.password')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={t('auth.passwordAuth.placeholders.createSecurePassword')}
                autoComplete="new-password"
                style={{
                  width: '100%',
                  padding: '0.75rem 3rem 0.75rem 0.75rem',
                  border: errors.password ? '2px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  backgroundColor: isLoading ? '#f9fafb' : 'white'
                }}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label={showPassword ? t('auth.passwordAuth.hidePassword') : t('auth.passwordAuth.showPassword')}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.password}</span>}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              <Lock size={18} />
              {t('auth.passwordAuth.confirmPassword')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder={t('auth.passwordAuth.placeholders.confirmYourPassword')}
                autoComplete="new-password"
                style={{
                  width: '100%',
                  padding: '0.75rem 3rem 0.75rem 0.75rem',
                  border: errors.confirmPassword ? '2px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  backgroundColor: isLoading ? '#f9fafb' : 'white'
                }}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
                aria-label={showConfirmPassword ? t('auth.passwordAuth.hidePassword') : t('auth.passwordAuth.showPassword')}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.confirmPassword && <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>{errors.confirmPassword}</span>}
          </div>

          {/* General Error */}
          {errors.general && (
            <div style={{ 
              color: '#ef4444', 
              fontSize: '0.875rem',
              textAlign: 'center',
              padding: '0.75rem',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px'
            }}>
              {errors.general}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: isLoading ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              marginTop: '0.5rem'
            }}
          >
            {isLoading ? t('auth.passwordAuth.buttons.creatingAccount') : t('auth.passwordAuth.createNewAccount')}
          </button>
        </form>

        {/* Login Link */}
        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem'
        }}>
          <p style={{ margin: 0, color: '#6b7280' }}>
            {t('auth.passwordAuth.buttons.alreadyHaveAccount')}{' '}
            <Link to="/auth" style={{
              color: '#3b82f6',
              textDecoration: 'none',
              fontWeight: '500'
            }}>
              {t('auth.passwordAuth.buttons.signInHere')}
            </Link>
          </p>
        </div>

        {/* Language Selector - Next Row Under Buttons */}
        <div className="auth-language-selector-bottom">
          <LanguageSelector variant="flags" showLabel={false} />
        </div>
      </div>

      {/* Toast Notification */}
      {toast.isVisible && (
        <div style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: 1000
        }}>
          <ToastNotificationComponent
            notification={{
              id: 'auth-toast',
              type: 'General',
              message: toast.message,
              timestamp: new Date(),
              severity: toast.type === 'success' ? 'success' : toast.type === 'error' ? 'error' : 'info'
            }}
            onClose={hideToast}
          />
        </div>
      )}
    </div>
  );
};

export default PasswordRegistrationPage;